
import React from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Eye,
  GraduationCap,
  HeartHandshake,
  Lightbulb,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
} from "lucide-react";

const schoolContent = {
  name: "ST. XAVIER'S PUBLIC SCHOOL",
  subtitle: "A Journey of Excellence & Values",

  description:
    "Academic excellence, all-round development, fostering ethical and religious values, and developing a sense of social responsibility are among the defining characteristics of education at St. Xavier's School. We continue our journey towards a brighter future, believing that dedicated service in education is a meaningful contribution to society.",

  mission:
    "Education plays a vital role in shaping society and determining the progress of a nation. Recognizing the importance of English as a global language, St. Xavier's Group of Schools strives to maintain high standards of English teaching, helping students achieve academic success and confidently meet the challenges of an evolving world.",

  vision:
    "St. Xavier's School, known as the Pride of Purvanchal, is an English Medium School committed to educational and spiritual traditions. Education here is not merely preparation for examinations; it is preparation for life. We encourage independent thinking, meaningful decisions, strong personal values, faith and responsibility towards others. Equal opportunities are offered to every learner, irrespective of caste, creed or colour.",
};

const images = [
  {
    src: "/image9.png",
    alt: "St. Xavier's School Campus",
  },
  {
    src: "/image7.png",
    alt: "Students participating in learning activities",
  },
  {
    src: "/image4.png",
    alt: "School sports activities",
  },
  {
    src: "/image6.png",
    alt: "School cultural activities",
  },
];

const highlights = [
  {
    title: "Our Objective",
    description:
      "To encourage academic excellence, all-round development, ethical values and a strong sense of social responsibility.",
    icon: Target,
    color: "amber",
  },
  {
    title: "Our Mission",
    description:
      "To provide quality education and strong English language skills that prepare students for academic achievement and future opportunities.",
    icon: GraduationCap,
    color: "red",
  },
  {
    title: "Our Vision",
    description:
      "To develop confident, thoughtful and responsible individuals who are prepared for life, not only examinations.",
    icon: Eye,
    color: "amber",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0 },
};

export default function WelcomeSection() {
  return (
    <section className="relative isolate w-full overflow-hidden bg-[#FFFCF9] py-10 sm:py-12 lg:py-14">

      {/* BACKGROUND DECORATIONS */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-20 h-80 w-80 rounded-full bg-red-100/60 blur-3xl" />

        <div className="absolute -right-32 top-1/3 h-96 w-96 rounded-full bg-amber-100/60 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-4 sm:px-7 lg:px-10 xl:px-16">

        {/* SECTION HEADING */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-white px-5 py-2 text-[10px] font-extrabold uppercase tracking-[0.17em] text-red-800 shadow-sm sm:text-xs">
            <Sparkles size={14} className="text-amber-500" />
            Discover Our Institution
          </span>

          <h1 className="mt-4 font-serif text-[28px] font-black leading-tight tracking-tight text-slate-950 sm:text-[37px] lg:whitespace-nowrap lg:text-[43px] xl:text-[51px]">
            Welcome to{" "}
            <span className="text-red-800">
              St. Xavier&apos;s
            </span>{" "}
            Public School
          </h1>

          <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-red-800 via-red-500 to-amber-400" />

          <p className="mt-4 font-serif text-[15px] font-medium italic text-slate-600 sm:text-lg">
            {schoolContent.subtitle}
          </p>
        </motion.div>

        {/* INTRODUCTION AND IMAGE GALLERY */}
        <div className="grid items-center gap-8 lg:grid-cols-[0.94fr_1.06fr] lg:gap-12">

          {/* LEFT SIDE */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <span className="h-[3px] w-9 rounded-full bg-amber-500" />

              <p className="text-[11px] font-extrabold uppercase tracking-[0.17em] text-amber-700">
                The Pride of Purvanchal
              </p>
            </div>

            <h2 className="font-serif text-2xl font-bold leading-tight text-slate-950 sm:text-3xl">
              Inspiring Minds,
              <span className="block text-red-800">
                Building Character
              </span>
            </h2>

            <p className="mt-5 text-[14px] leading-[1.95] text-slate-600 sm:text-[15px]">
              {schoolContent.description}
            </p>

            <p className="mt-4 text-[14px] leading-[1.95] text-slate-600 sm:text-[15px]">
              Through a balanced approach to classroom
              learning, creativity, discipline and
              co-curricular participation, we aim to
              nurture confident learners who can think
              independently and contribute positively
              to the world around them.
            </p>

            {/* HIGHLIGHT */}
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-4 py-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <HeartHandshake size={21} />
              </div>

              <div>
                <h3 className="text-sm font-extrabold text-slate-900">
                  Education with Purpose
                </h3>

                <p className="mt-1 text-xs leading-6 text-slate-600">
                  Nurturing knowledge, responsibility,
                  ethical values and opportunities for
                  every child to grow.
                </p>
              </div>
            </div>

            {/* VALUES */}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-white px-3 py-3 text-xs font-semibold text-slate-700 shadow-sm">
                <ShieldCheck size={18} className="shrink-0 text-red-800" />
                Strong Moral Values
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-white px-3 py-3 text-xs font-semibold text-slate-700 shadow-sm">
                <BookOpen size={18} className="shrink-0 text-red-800" />
                Quality Education
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-white px-3 py-3 text-xs font-semibold text-slate-700 shadow-sm">
                <Lightbulb size={18} className="shrink-0 text-red-800" />
                Independent Thinking
              </div>

              <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-white px-3 py-3 text-xs font-semibold text-slate-700 shadow-sm">
                <Users size={18} className="shrink-0 text-red-800" />
                Equal Opportunities
              </div>
            </div>
          </motion.div>

          {/* RIGHT SIDE - CLEAN IMAGE GALLERY */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="pointer-events-none absolute -right-3 -top-3 h-28 w-28 rounded-[28px] border-[7px] border-amber-300 sm:-right-4 sm:-top-4" />

            <div className="pointer-events-none absolute -bottom-4 -left-3 h-28 w-28 rounded-[28px] bg-red-800 sm:-bottom-5 sm:-left-5" />

            <div className="relative z-10 grid grid-cols-2 gap-3 rounded-[25px] border-[8px] border-white bg-white p-1.5 shadow-[0_20px_55px_rgba(100,25,25,0.13)] sm:gap-4 sm:rounded-[30px] sm:p-2.5">
              {images.map((image, index) => (
                <div
                  key={image.src}
                  className={`group relative overflow-hidden rounded-xl bg-slate-100 sm:rounded-2xl ${
                    index === 0 || index === 3
                      ? "h-[170px] sm:h-[235px] lg:h-[245px]"
                      : "h-[170px] sm:h-[235px] lg:h-[245px]"
                  }`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.045]"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* MISSION AND VISION */}
        <div className="mt-12 grid gap-6 lg:grid-cols-2 lg:gap-8">

          {/* MISSION */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="group relative overflow-hidden rounded-[24px] border border-red-100 bg-white p-6 shadow-[0_12px_40px_rgba(90,30,30,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-7"
          >
            <div className="absolute right-0 top-0 h-28 w-28 rounded-bl-[100px] bg-red-50" />

            <div className="relative flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-800 text-white">
                <GraduationCap size={23} />
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.17em] text-red-700">
                  What We Strive For
                </p>

                <h3 className="mt-1 font-serif text-2xl font-bold text-slate-950">
                  Our Mission
                </h3>
              </div>
            </div>

            <div className="mt-5 h-px bg-gradient-to-r from-red-200 via-amber-100 to-transparent" />

            <p className="relative mt-5 text-[13px] leading-[1.9] text-slate-600 sm:text-[14px]">
              {schoolContent.mission}
            </p>
          </motion.div>

          {/* VISION */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="group relative overflow-hidden rounded-[24px] border border-amber-100 bg-white p-6 shadow-[0_12px_40px_rgba(90,30,30,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-7"
          >
            <div className="absolute right-0 top-0 h-28 w-28 rounded-bl-[100px] bg-amber-50" />

            <div className="relative flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-500 text-white">
                <Eye size={23} />
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.17em] text-amber-700">
                  The Future We Envision
                </p>

                <h3 className="mt-1 font-serif text-2xl font-bold text-slate-950">
                  Our Vision
                </h3>
              </div>
            </div>

            <div className="mt-5 h-px bg-gradient-to-r from-amber-200 via-red-100 to-transparent" />

            <p className="relative mt-5 text-[13px] leading-[1.9] text-slate-600 sm:text-[14px]">
              {schoolContent.vision}
            </p>
          </motion.div>
        </div>

        {/* BOTTOM HIGHLIGHT CARDS */}
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {highlights.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.08,
                }}
                className={`group relative overflow-hidden rounded-2xl border bg-white px-5 py-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                  item.color === "red"
                    ? "border-red-100"
                    : "border-amber-100"
                }`}
              >
                <div
                  className={`absolute left-0 top-0 h-full w-1 ${
                    item.color === "red"
                      ? "bg-red-800"
                      : "bg-amber-400"
                  }`}
                />

                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                      item.color === "red"
                        ? "bg-red-50 text-red-800"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    <Icon size={21} />
                  </div>

                  <div>
                    <h3 className="text-[15px] font-extrabold text-slate-900">
                      {item.title}
                    </h3>

                    <p className="mt-2 text-[12px] leading-[1.75] text-slate-600">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
